import { PureComponent } from 'react';
import PropTypes from 'prop-types';
import { Typography, withStyles } from '@material-ui/core';
import { RECURRING_TYPES } from 'constants/oneOnOne';
import CustomButton from '../../shared/customButton';
import OneOnOneRecords from '../../shared/oneOnOneRecords';
import AlertDialog from '../../shared/alertDialog';
import NotificationCard from '../../shared/notificationCard';
import Filters from '../../shared/filters';
import OneOnOneRequestCard from '../../shared/oneOnOneRequestCard';
import ManageOneOnOne from '../../shared/manageOneOnOne';
import ManageTasks from '../../shared/manageTasks';
import AddImpressionDialog from '../../shared/addImpressionDialog';
import ConditionalTooltip from '../../shared/conditionalTooltip';
import { PERMISSION_CODES } from '../../../constants/permissions';
import http from '../../../utility/http';
import {
  isArray,
  isObjectEmpty,
  getObjectToNumberArray,
  isArrayEmpty,
  isUserDeactivated,
} from '../../../utility/helpers';
import { prepareTasksForSave } from '../../../utility/tasks';
import {
  hasNextPage,
  parseDuplicateParameters,
  parseQueryParams,
  showSuccessMessage,
  hasSelectedFiltersCheck,
} from '../../../utility/uiUtils';
import {
  hasScopedAuthPermissionCheck,
  isUserProfileAccessibleCheck,
} from '../../../utility/permissions';
import {
  prepareManageOneOnOneSuccessMessages,
  prepareOneOnOneTasksAvailableUsers,
  prepareOneOnOneRecordsData,
} from '../../../utility/oneOnOne';
import {
  EVENT_ACTION_TYPES,
  tagManagerDataLayer,
} from '../../../utility/tagManager';
import { onSaveCreatableTag } from '../../../utility/tagUtils';
import {
  API_ONE_ON_ONE_TOPICS,
  api_one_on_one_record,
  api_one_on_one_tasks,
  api_one_on_one_topic,
  api_user_one_on_one,
} from '../../../constants/apiRoutes';
import { GENERAL_CATEGORY_ID } from '../../../constants/tags';
import { sticky } from '../../../constants/helperCssRules';
import {
  PARAMS,
  PEOPLE_ONE_ON_ONE_DEFAULT_PARAMS,
  PAGE_WHITELISTED_PARAMS,
} from '../../../constants/pages';
import { preparePeopleOneOnOnePageFilters } from './config';

const styles = ({ palette: { primary }, spacing }) => ({
  header: {
    ...sticky(primary.white, 170),
  },
  description: {
    marginBottom: spacing(4),
  },
  addButtonWrapper: {
    display: 'flex',
    alignItems: 'center',
    marginLeft: spacing(2),
  },
  addButton: {
    minWidth: 115,
    width: 'max-content',
  },
  loadMoreWrapper: {
    paddingTop: spacing(4),
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
  },
  loadMoreButton: {
    border: `1px solid ${primary.bluish3}`,
    borderRadius: 44,
    color: primary.bluish4,
    lineHeight: '24px',
    justifySelf: 'center',
    padding: spacing(1, 4),
    maxHeight: 32,
  },
  actions: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    marginBottom: spacing(4),
  },
  requestCard: {
    marginBottom: spacing(4),
  },
  editorLabelWrapper: {
    justifyContent: 'space-between',
  },
  editorLabel: {
    fontFamily: 'ProximaNova-Bold',
    fontSize: 20,
    lineHeight: '24px',
  },
  disabledUserAction: {
    cursor: 'not-allowed',
  },
});

const { PAGE, COMPLETION_STATUS, TAGS } = PARAMS;

class PeopleOneOnOnePage extends PureComponent {
  state = {
    isInitialLoad: true,
    isLoading: false,
    [PAGE]: 1,
    currentRecord: null,
    isOneOnOneDialogOpened: false,
    isReschedule: false,
    newRecurring: false,
    dismissRequestOpened: false,
    isOneOnOneRequest: false,
    isAddImpressionOpened: false,
    isManageTasksOpened: false,
    filters: {
      [COMPLETION_STATUS]: [],
      [TAGS]: [],
    },
  };

  componentDidMount() {
    this.getInitialData();
  }

  componentWillUnmount() {
    const {
      clearPersonOneOnOneRecords,
      clearOneOnOneTopics,
      clearPersonOneOnOneRequest,
      clearTagsCategories,
      clearPageQuickFilters,
    } = this.props;

    clearPersonOneOnOneRecords();
    clearOneOnOneTopics();
    clearPersonOneOnOneRequest();
    clearTagsCategories();
    clearPageQuickFilters();
  }

  getInitialData = () => {
    const {
      location,
      auth,
      user,
      getPageQuickFilters,
      getTagsCategories,
      getOneOnOneTopics,
      getPersonOneOnOneRequest,
    } = this.props;
    const { search } = location;
    const params = parseQueryParams(
      search,
      PAGE_WHITELISTED_PARAMS.PEOPLE_ONE_ON_ONE
    );

    if (!isObjectEmpty(params)) {
      return this.setState(
        {
          filters: {
            [COMPLETION_STATUS]: params[COMPLETION_STATUS]
              ? [params[COMPLETION_STATUS]]
              : [],
            [TAGS]: params[TAGS]
              ? [...(isArray(params[TAGS]) ? params[TAGS] : [params[TAGS]])]
              : [],
          },
        },
        () => {
          return Promise.all([
            this.handlePageChange(false),
            ...(auth.id === user.report_to
              ? [getPersonOneOnOneRequest(user.id)]
              : []),
            getOneOnOneTopics(),
            getTagsCategories(),
            getPageQuickFilters(),
          ]).then(() => {
            this.setState({ isInitialLoad: false });
          });
        }
      );
    }

    return Promise.all([
      this.handlePageChange(),
      ...(auth.id === user.report_to
        ? [getPersonOneOnOneRequest(user.id)]
        : []),
      getOneOnOneTopics(),
      getTagsCategories(),
      getPageQuickFilters(),
    ]).then(() => {
      this.setState({ isInitialLoad: false });
    });
  };

  canAccessUserProfileCheck = user => {
    const { auth, accessControl } = this.props;
    return isUserProfileAccessibleCheck(
      accessControl,
      auth.id,
      user?.id || user
    );
  };

  canAccessDetailsCheck = (userId, requiredPermission) => {
    const { auth, accessControl } = this.props;
    return hasScopedAuthPermissionCheck(
      requiredPermission,
      accessControl,
      auth.id,
      userId
    );
  };

  handlePageChange = (isLoadMore = false) => {
    const { location, navigate, user, getPersonOneOnOneRecords } = this.props;
    const { page, filters } = this.state;
    const params = {
      page,
      ...filters,
    };

    const query = parseDuplicateParameters(params);

    if (location.search !== `?${query}`) {
      navigate(`/people/${user.id}/one-on-one/?${query}`, {
        replace: true,
        state: location.state || null,
      });
    }

    return getPersonOneOnOneRecords(user.id, isLoadMore, params);
  };

  handleOpenOneOnOneDialog = () => {
    this.setState({ isOneOnOneDialogOpened: true });
  };

  onCloseDrawer = () => {
    const { newRecurring } = this.state;

    this.setState(
      {
        isOneOnOneDialogOpened: false,
        currentRecord: null,
        isReschedule: false,
        newRecurring: false,
        isOneOnOneRequest: false,
        ...(newRecurring
          ? { [PAGE]: PEOPLE_ONE_ON_ONE_DEFAULT_PARAMS[PAGE] }
          : {}),
      },
      newRecurring ? this.handlePageChange : () => {}
    );
  };

  onSuccessAction = () => {
    const { clearPersonOneOnOneRequest } = this.props;
    const { isOneOnOneRequest } = this.state;

    if (isOneOnOneRequest) {
      clearPersonOneOnOneRequest();
    }

    this.onCloseDrawer();

    return Promise.resolve();
  };

  handleSaveRecord = async values => {
    const { translations } = this.props;
    const { currentRecord, isReschedule, isOneOnOneRequest, newRecurring } =
      this.state;
    const { user, hasCustomTopic, customTopic, ...rest } = values;
    const payload = { ...rest };

    if (hasCustomTopic) {
      const { data } = customTopic?.id
        ? await http.patch(api_one_on_one_topic(customTopic?.id), {
            name: customTopic?.name,
          })
        : await http.post(API_ONE_ON_ONE_TOPICS, {
            name: customTopic?.name,
          });

      if (data?.id) {
        payload.agenda = [...payload.agenda, data.id];
      }
    }

    if (currentRecord && !newRecurring) {
      await http.patch(api_one_on_one_record(currentRecord?.id), {
        ...payload,
        user: user.id,
      });
    } else {
      await http.post(api_user_one_on_one(user.id), {
        ...payload,
      });
      const { AddOneOnOne } = EVENT_ACTION_TYPES;
      tagManagerDataLayer(AddOneOnOne.action, AddOneOnOne.name);
    }

    return this.onSuccessAction().then(() => {
      showSuccessMessage(
        prepareManageOneOnOneSuccessMessages(
          translations.successMessages,
          currentRecord?.id,
          isOneOnOneRequest,
          isReschedule
        )
      );
      return this.setState(
        { [PAGE]: PEOPLE_ONE_ON_ONE_DEFAULT_PARAMS[PAGE] },
        this.handlePageChange
      );
    });
  };

  handleAcceptOneOnOneRequest = () => {
    const { personOneOnOneRequest } = this.props;

    return this.setState(
      {
        currentRecord: personOneOnOneRequest,
        isOneOnOneRequest: true,
      },
      () => this.handleOpenOneOnOneDialog()
    );
  };

  handleEditRecord = record => () => {
    this.setState({ currentRecord: record }, () =>
      this.handleOpenOneOnOneDialog()
    );
  };

  handleReschedule = record => () => {
    this.setState({ currentRecord: record, isReschedule: true }, () =>
      this.handleOpenOneOnOneDialog()
    );
  };

  toggleAddImpression =
    (record = null, updatedState = {}, cb = () => {}) =>
    () => {
      this.setState(
        prevState => ({
          isAddImpressionOpened: !prevState.isAddImpressionOpened,
          currentRecord: record,
          ...updatedState,
        }),
        cb
      );
    };

  onSaveImpression = async data => {
    const { translations, user } = this.props;
    const { currentRecord } = this.state;
    const { impression, nextRecurring, stoppedRecurring, notify } = data;
    const { recurring, tags, agenda, note, tasks, plans } = currentRecord;
    const noteText = note ? note.text : '';
    const { AddOneOnOne } = EVENT_ACTION_TYPES;

    return http
      .patch(api_one_on_one_record(currentRecord?.id), {
        meeting_impression: impression,
        recurring: stoppedRecurring ? RECURRING_TYPES[0].id : recurring,
        tasks: prepareTasksForSave(tasks),
        user: user.id,
      })
      .then(async () => {
        showSuccessMessage(translations.addImpressionDialog.success);
        if (!stoppedRecurring) {
          const params = {
            meeting_time: nextRecurring,
            recurring: stoppedRecurring ? RECURRING_TYPES[0].id : recurring,
            tags: getObjectToNumberArray(tags),
            plans: getObjectToNumberArray(plans),
            agenda: getObjectToNumberArray(agenda),
            note: noteText,
            user: user.id,
            notify,
          };
          try {
            await http.post(api_user_one_on_one(user.id), {
              ...params,
            });
            showSuccessMessage(translations.successMessages.general.create);
            tagManagerDataLayer(AddOneOnOne.action, AddOneOnOne.name);
          } finally {
            this.toggleAddImpression(
              null,
              { [PAGE]: PEOPLE_ONE_ON_ONE_DEFAULT_PARAMS[PAGE] },
              this.handlePageChange
            )();
          }
        } else {
          this.toggleAddImpression(
            null,
            { [PAGE]: PEOPLE_ONE_ON_ONE_DEFAULT_PARAMS[PAGE] },
            this.handlePageChange
          )();
        }
      }, this.toggleAddImpression());
  };

  onSaveAndCreate = async (impression, tasks) => {
    const { translations } = this.props;
    const { currentRecord } = this.state;

    await http
      .patch(api_one_on_one_record(currentRecord.id), {
        meeting_impression: impression,
        tasks: prepareTasksForSave(tasks),
        user: currentRecord.user.id,
      })
      .then(async () => {
        showSuccessMessage(translations.addImpressionDialog.success);
        this.setState({
          isAddImpressionOpened: false,
          isOneOnOneDialogOpened: true,
          newRecurring: true,
        });
      }, this.toggleAddImpression());
  };

  handleDeleteRecord = record => () => {
    const { setDialogVisibility } = this.props;

    this.setState({ currentRecord: record }, () => {
      setDialogVisibility({
        dialogName: 'deleteOneOnOneRecord',
        opened: true,
      });
    });
  };

  onDeleteRecord = () => {
    const { translations, deletePersonOneOnOneRecord } = this.props;
    const { currentRecord } = this.state;

    return http.delete(api_one_on_one_record(currentRecord?.id)).then(
      () => {
        deletePersonOneOnOneRecord(currentRecord?.id);
        this.onCancelDeleteRecord();
        this.onSuccessAction();
        showSuccessMessage(
          translations.deleteOneOnOneRecordDialog.deleteSuccess
        );

        this.setState(
          { [PAGE]: PEOPLE_ONE_ON_ONE_DEFAULT_PARAMS[PAGE] },
          this.handlePageChange
        );
      },
      () => this.onCancelDeleteRecord()
    );
  };

  onCancelDeleteRecord = () => {
    const { setDialogVisibility } = this.props;
    setDialogVisibility({
      dialogName: 'deleteOneOnOneRecord',
      opened: false,
    });
  };

  toggleDismissOneOnOneRequest = (record = null) =>
    this.setState(prevState => ({
      dismissRequestOpened: !prevState.dismissRequestOpened,
      currentRecord: record,
    }));

  onDismissOneOnOneRequest = () => {
    const { translations, clearPersonOneOnOneRequest } = this.props;
    const { currentRecord } = this.state;

    return http.delete(api_one_on_one_record(currentRecord?.id)).then(() => {
      clearPersonOneOnOneRequest();
      this.toggleDismissOneOnOneRequest();
      showSuccessMessage(
        translations.dismissOneOnOneRequestDialog.dismissSuccess
      );
    });
  };

  onLoadMore = () => {
    return this.setState(
      prevState => ({ ...prevState, [PAGE]: prevState[PAGE] + 1 }),
      () => this.handlePageChange(true)
    );
  };

  handleApplyFilters = filters => {
    this.setState(
      {
        [PAGE]: PEOPLE_ONE_ON_ONE_DEFAULT_PARAMS[PAGE],
        filters,
        isLoading: true,
      },
      () =>
        this.handlePageChange().then(() => this.setState({ isLoading: false }))
    );
  };

  onCreateTag = async tagName => {
    const { addMultipleTags } = this.props;
    const tags = await onSaveCreatableTag(tagName);
    const [tag] = tags.create;

    addMultipleTags({ categoryId: GENERAL_CATEGORY_ID, tags });

    return tag;
  };

  handleGoToDetailsPage = ({ id }) => {
    const { navigate } = this.props;

    navigate(`/1-1/${id}/details`);
  };

  handleOpenManageTasks = record => () => {
    this.setState({ currentRecord: record, isManageTasksOpened: true });
  };

  handleCloseManageTasks = () => {
    this.setState({ currentRecord: null, isManageTasksOpened: false });
  };

  onSaveTasks = async values => {
    const { translations, auth } = this.props;
    const { currentRecord } = this.state;

    const isCreatedForCurrentUser = auth.id === currentRecord.user.id;
    const data = {
      tasks: prepareTasksForSave(values.tasks),
      user: currentRecord.user.id,
    };

    if (isCreatedForCurrentUser) {
      await http.post(api_one_on_one_tasks(currentRecord.id), data);
    } else {
      await http.patch(api_one_on_one_record(currentRecord.id), data);
    }
    showSuccessMessage(translations.manageTasksDialog.successMessage);

    return this.setState(
      { [PAGE]: PEOPLE_ONE_ON_ONE_DEFAULT_PARAMS[PAGE] },
      this.handlePageChange
    );
  };

  goToUserProfilePage = userId => this.props.navigate(`/people/${userId}`);

  handleClearNotifications = () => {
    const { navigate, user } = this.props;
    const { page, filters } = this.state;

    const params = {
      page,
      ...filters,
    };
    const query = parseDuplicateParameters(params);

    navigate(`/people/${user.id}/one-on-one/?${query}`, {
      replace: true,
      state: null,
    });
  };

  render() {
    const {
      classes,
      translations,
      location,
      auth,
      accessControl,
      dialogs,
      personOneOnOne,
      topics,
      user,
      categories,
      pageQuickFilters,
      personOneOnOneRequest,
    } = this.props;
    const {
      currentRecord,
      isInitialLoad,
      isLoading,
      isReschedule,
      newRecurring,
      dismissRequestOpened,
      isOneOnOneRequest,
      isOneOnOneDialogOpened,
      isAddImpressionOpened,
      filters,
      isManageTasksOpened,
    } = this.state;
    const {
      description,
      addButtonLabel,
      deleteOneOnOneRecordDialog,
      dismissOneOnOneRequestDialog,
      noRecords,
      requestCard,
    } = translations;
    const { deleteOneOnOneRecordOpened } = dialogs;
    const isDeactivatedUser = isUserDeactivated(user);
    const canEdit = hasScopedAuthPermissionCheck(
      PERMISSION_CODES.ONE_ON_ONE_EDIT,
      accessControl,
      auth.id,
      user?.id
    );
    const canDelete = hasScopedAuthPermissionCheck(
      PERMISSION_CODES.ONE_ON_ONE_DELETE,
      accessControl,
      auth.id,
      user?.id
    );
    const hasLoadMore = hasNextPage(personOneOnOne);
    const isFilterActive = hasSelectedFiltersCheck(filters);

    return (
      <div>
        {!isInitialLoad && (
          <div className={classes.header}>
            <Typography variant="body2" className={classes.description}>
              {description}
            </Typography>
            <div className={classes.actions}>
              <Filters
                className={classes.filters}
                translations={translations.filters}
                selectedFilters={filters}
                filters={preparePeopleOneOnOnePageFilters(
                  translations,
                  categories,
                  pageQuickFilters
                )}
                onApplyFilters={this.handleApplyFilters}
              />
              {canEdit && (
                <div className={classes.addButtonWrapper}>
                  <ConditionalTooltip
                    className={classes.disabledUserAction}
                    message={translations.disabledAddActionTooltip}
                    addTooltip={isDeactivatedUser}
                  >
                    <CustomButton
                      className={classes.addButton}
                      disabled={isDeactivatedUser}
                      onClick={this.handleOpenOneOnOneDialog}
                      type="addRoundedNew"
                    >
                      {addButtonLabel}
                    </CustomButton>
                  </ConditionalTooltip>
                </div>
              )}
            </div>
          </div>
        )}
        <div>
          {!isDeactivatedUser && personOneOnOneRequest && (
            <OneOnOneRequestCard
              className={classes.requestCard}
              translations={requestCard}
              request={personOneOnOneRequest}
              onAcceptRequest={this.handleAcceptOneOnOneRequest}
              onDismissRequest={this.toggleDismissOneOnOneRequest}
            />
          )}
          <OneOnOneRecords
            translations={translations.oneOnOneRecords}
            records={prepareOneOnOneRecordsData(
              personOneOnOne.results,
              auth,
              this.canAccessUserProfileCheck,
              this.canAccessDetailsCheck
            )}
            notifications={location?.state?.notifications}
            goToUserProfilePage={this.goToUserProfilePage}
            canAccessUserProfileCheck={this.canAccessUserProfileCheck}
            currentUser={auth}
            isDisabled={isDeactivatedUser}
            canEdit={canEdit}
            canDelete={canDelete}
            onCardClickHandler={this.handleGoToDetailsPage}
            onDeleteRecord={this.handleDeleteRecord}
            onEditRecord={this.handleEditRecord}
            onRescheduleRecord={this.handleReschedule}
            onAddImpression={this.toggleAddImpression}
            onManageTasks={this.handleOpenManageTasks}
            onClearNotifications={this.handleClearNotifications}
          />
          {personOneOnOne?.results &&
            !isArrayEmpty(personOneOnOne.results) &&
            hasLoadMore && (
              <div className={classes.loadMoreWrapper}>
                <CustomButton
                  type="addWithTextRounded"
                  className={classes.loadMoreButton}
                  onClick={this.onLoadMore}
                >
                  {translations.loadMore}
                </CustomButton>
              </div>
            )}
          <NotificationCard
            shouldFade={
              !isInitialLoad &&
              !isLoading &&
              personOneOnOne?.results &&
              isArrayEmpty(personOneOnOne.results)
            }
            title={isFilterActive ? '' : noRecords.title}
            content={
              isFilterActive
                ? noRecords.noSearchResultsMessage
                : noRecords.message
            }
          />
        </div>
        <AlertDialog
          translations={deleteOneOnOneRecordDialog}
          isOpened={deleteOneOnOneRecordOpened}
          onClose={this.onCancelDeleteRecord}
          onConfirm={this.onDeleteRecord}
          isWarning
        />
        <AlertDialog
          translations={dismissOneOnOneRequestDialog}
          isOpened={dismissRequestOpened}
          onClose={this.toggleDismissOneOnOneRequest}
          onConfirm={this.onDismissOneOnOneRequest}
          isWarning
        />
        <ManageOneOnOne
          translations={translations.oneOnOneRecordDialog}
          isOpened={isOneOnOneDialogOpened}
          topics={topics}
          record={currentRecord}
          categories={categories}
          createdFor={user}
          auth={auth}
          accessControl={accessControl}
          isOneOnOneRequest={isOneOnOneRequest}
          isReschedule={isReschedule}
          newRecurring={newRecurring}
          onCreateTag={this.onCreateTag}
          onDelete={this.handleDeleteRecord(currentRecord)}
          onClose={this.onCloseDrawer}
          onSave={this.handleSaveRecord}
        />
        {currentRecord && (
          <ManageTasks
            translations={translations.manageTasksDialog}
            isOpened={isManageTasksOpened}
            record={currentRecord}
            availableUsers={prepareOneOnOneTasksAvailableUsers(currentRecord)}
            onClose={this.handleCloseManageTasks}
            onSave={this.onSaveTasks}
          />
        )}
        <AddImpressionDialog
          translations={translations.addImpressionDialog}
          isOpened={isAddImpressionOpened}
          recordId={currentRecord?.id}
          currentRecord={isAddImpressionOpened ? currentRecord : null}
          onCancel={this.toggleAddImpression()}
          onConfirm={this.onSaveImpression}
          onSaveAndCreate={this.onSaveAndCreate}
        />
      </div>
    );
  }
}

PeopleOneOnOnePage.defaultProps = {
  personOneOnOneRequest: null,
};

PeopleOneOnOnePage.propTypes = {
  classes: PropTypes.object.isRequired,
  translations: PropTypes.object.isRequired,
  location: PropTypes.object.isRequired,
  dialogs: PropTypes.shape({}).isRequired,
  auth: PropTypes.shape({}).isRequired,
  accessControl: PropTypes.shape({}).isRequired,
  navigate: PropTypes.func.isRequired,
  user: PropTypes.shape({}).isRequired,
  personOneOnOne: PropTypes.shape({}).isRequired,
  personOneOnOneRequest: PropTypes.shape({}),
  categories: PropTypes.arrayOf(PropTypes.shape({})).isRequired,
  pageQuickFilters: PropTypes.arrayOf(PropTypes.shape({})).isRequired,
  topics: PropTypes.arrayOf(PropTypes.shape({})).isRequired,
  getPersonOneOnOneRecords: PropTypes.func.isRequired,
  getOneOnOneTopics: PropTypes.func.isRequired,
  setDialogVisibility: PropTypes.func.isRequired,
  getPersonOneOnOneRequest: PropTypes.func.isRequired,
  addMultipleTags: PropTypes.func.isRequired,
  clearOneOnOneTopics: PropTypes.func.isRequired,
  clearPersonOneOnOneRequest: PropTypes.func.isRequired,
  clearPersonOneOnOneRecords: PropTypes.func.isRequired,
  deletePersonOneOnOneRecord: PropTypes.func.isRequired,
  getTagsCategories: PropTypes.func.isRequired,
  clearTagsCategories: PropTypes.func.isRequired,
  getPageQuickFilters: PropTypes.func.isRequired,
};

export default withStyles(styles)(PeopleOneOnOnePage);
